import React, { useMemo } from 'react';

/**
 * Rolling 30-day strip showing the number of items on each of the last 30
 * days (aligned with backend data retention).
 *
 * Layout: 5 columns × 6 rows. The leftmost cell is the oldest day in the
 * window (today − 29), the bottom-right cell is today.
 *
 * Single-day mode (default): click a day with items to filter; click the
 * same day again to clear.
 *
 * Range mode (range=true, used by Rubrics to match Sort Papers): first
 * click sets the start day, second click sets the end day. Days between
 * light up. A click after a finished range starts a new one. Empty days
 * are clickable so a span can cover quiet days.
 *
 * Props:
 *   items              - array of records to count (e.g. folders, rubrics)
 *   dateField          - field on each record that holds the date (default 'test_date')
 *   selectedDate       - start day as 'YYYY-MM-DD' (or '' for none)
 *   selectedEndDate    - end day as 'YYYY-MM-DD' when range is on (or '')
 *   onDateChange       - single day: (dateStr) => void
 *                        range: (startStr, endStr) => void; clear is ('', '')
 *   range              - allow start/end day picking (default false)
 *   itemLabelSingular  - e.g. 'Folder'
 *   itemLabelPlural    - e.g. 'Folders'
 *   itemLabelSeparator - between count and label (default ' '; Mistakes uses '-')
 *   title              - optional header text shown above the calendar
 *   windowDays         - how many days to show (default 30)
 */
export default function DateFilterCalendar({
  items = [],
  dateField = 'test_date',
  selectedDate = '',
  selectedEndDate = '',
  onDateChange,
  range = false,
  itemLabelSingular = 'Item',
  itemLabelPlural = 'Items',
  itemLabelSeparator = ' ',
  title = null,
  windowDays = 30,
  // Optional { 'YYYY-MM-DD': count }. When set, day badges use these
  // totals instead of counting `items`.
  counts = null,
  // Number of grid columns. 5 is the spacious default used at full width;
  // narrow side-rail layouts pass 3 to stack days into a tall strip.
  columns = 5,
  // When true, show the NEXT `windowDays` days (tomorrow … tomorrow+windowDays-1)
  // instead of the trailing window ending today — used by the "Future Cards"
  // toggle so upcoming dated items become pickable. Defaults to the classic
  // trailing window so every other consumer is unchanged.
  future = false,
  // When true, every day shows its count as a number, including 0.
  showCount = false,
  // Optional node in the top-right slot. When set, it takes the place of
  // the date-range pill. Other calendars leave this empty and keep the pill.
  headerAside = null,
  // Tighter toolbar used by Messages. Other calendars stay at the roomy size.
  compact = false,
}) {
  const dateToKey = (d) => {
    if (typeof d === 'string') {
      const m = d.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`;
  };

  const countsByDate = useMemo(() => {
    if (counts && typeof counts === 'object') {
      const map = new Map();
      Object.entries(counts).forEach(([key, value]) => {
        const n = Number(value) || 0;
        if (key && n > 0) map.set(key, n);
      });
      return map;
    }
    const map = new Map();
    (items || []).forEach((it) => {
      const raw = it && it[dateField];
      if (!raw) return;
      const k = dateToKey(raw);
      map.set(k, (map.get(k) || 0) + 1);
    });
    return map;
  }, [items, dateField, counts]);

  // Build the rolling window: today − (windowDays-1) … today. Each cell
  // gets a "current" flag set to true for visual consistency with the
  // previous month-layout (kept since the renderer below reads it).
  const getWindowDays = () => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const days = [];
    if (future) {
      // Next `windowDays` days, starting tomorrow.
      for (let i = 1; i <= windowDays; i++) {
        const d = new Date(todayMidnight);
        d.setDate(todayMidnight.getDate() + i);
        days.push({ date: d, current: true });
      }
    } else {
      for (let i = windowDays - 1; i >= 0; i--) {
        const d = new Date(todayMidnight);
        d.setDate(todayMidnight.getDate() - i);
        days.push({ date: d, current: true });
      }
    }
    return days;
  };

  const handleDateClick = (date) => {
    const key = dateToKey(date);
    if (!range) {
      if (selectedDate === key) {
        onDateChange && onDateChange('');
      } else {
        onDateChange && onDateChange(key);
      }
      return;
    }
    // Same click order as Sort Papers: start, then end; a click after a
    // finished range starts again. Same-day second click clears.
    if (!selectedDate || selectedEndDate) {
      onDateChange && onDateChange(key, '');
      return;
    }
    if (key === selectedDate) {
      onDateChange && onDateChange('', '');
      return;
    }
    if (key < selectedDate) {
      onDateChange && onDateChange(key, selectedDate);
    } else {
      onDateChange && onDateChange(selectedDate, key);
    }
  };

  const clearSelection = () => {
    if (range) onDateChange && onDateChange('', '');
    else onDateChange && onDateChange('');
  };

  const todayKey = dateToKey(new Date());
  const days = getWindowDays();
  const startKey = selectedDate || '';
  const endKey = range ? (selectedEndDate || '') : '';
  const hasSelection = !!startKey;
  const formatDay = (key) => {
    if (!key) return '';
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const windowLabel = (() => {
    if (days.length === 0) return '';
    const first = days[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const last = days[days.length - 1].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${first} – ${last}`;
  })();
  const pill = range && startKey && endKey
    ? `${formatDay(startKey)} – ${formatDay(endKey)}`
    : startKey
      ? formatDay(startKey)
      : windowLabel;
  const headerTitle = range ? (title || 'Date range') : (title || `${future ? 'Next' : 'Last'} ${windowDays} days`);

  const rangePill = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: '0.68rem',
        fontWeight: 700,
        background: hasSelection ? 'rgba(37,99,235,0.12)' : 'rgba(16,185,129,0.12)',
        color: hasSelection ? '#1d4ed8' : '#15803d',
        border: hasSelection ? '1px solid rgba(37,99,235,0.45)' : '1px solid rgba(16,185,129,0.45)',
        whiteSpace: 'nowrap',
      }}
    >
      {pill}
    </span>
  );
  const titleNode = (
    <span style={{
      fontWeight: 700,
      fontSize: compact ? '0.68rem' : '0.85rem',
      letterSpacing: compact ? 0.8 : 0,
      textTransform: compact ? 'uppercase' : 'none',
      color: compact ? 'var(--text-secondary, #6b7280)' : 'var(--text-primary)',
      flexShrink: 0,
    }}>
      {headerTitle}
    </span>
  );

  return (
    <div
      style={{
        background: 'var(--card-bg, white)',
        border: '1px solid var(--border-color, #e0e0e0)',
        borderRadius: '12px',
        padding: compact ? '8px 10px' : '6px',
        boxShadow: compact ? '0 1px 2px rgba(15,23,42,0.04)' : '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: compact ? '12px' : '8px',
        borderColor: compact ? 'var(--border-color, #e5e7eb)' : undefined,
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      {/* Compact header sits on the day row: title at the left end,
          actions at the right end. Other calendars keep the title above. */}
      {!compact && (
        <div style={{ marginBottom: 6, padding: '0 4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {titleNode}
            {headerAside != null ? headerAside : rangePill}
          </div>
          {range && (
            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              Click a start day, then an end day.
            </div>
          )}
        </div>
      )}

      <div style={compact ? { display: 'flex', alignItems: 'center', gap: 10 } : undefined}>
      {compact && titleNode}

      {/* Day cells — narrow layouts pass columns=3 for a tall strip;
          full-width usage falls back to a 5-wide grid (6 rows for 30 days). */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: compact ? '4px' : '6px', flex: compact ? 1 : undefined, minWidth: 0, width: '100%' }}>
        {days.map((cell, idx) => {
          const key = dateToKey(cell.date);
          const isToday = todayKey === key;
          const isEndpoint = !!(startKey && (key === startKey || key === endKey));
          const isInRange = !!(range && startKey && endKey && key > startKey && key < endKey);
          const isSelected = !range && startKey === key;
          const active = isEndpoint || isSelected;
          const count = countsByDate.get(key) || 0;
          const hasItems = count > 0;
          const clickable = range || hasItems;
          return (
            <div
              key={idx}
              onClick={() => clickable && handleDateClick(cell.date)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: compact ? '4px 2px' : '5px 4px',
                minHeight: compact ? '52px' : '40px',
                borderRadius: '8px',
                cursor: clickable ? 'pointer' : 'default',
                background: active
                  ? 'linear-gradient(135deg,#3b82f6,#2563eb)'
                  : isInRange
                  ? 'rgba(59,130,246,0.22)'
                  : isToday
                  ? 'rgba(59,130,246,0.07)'
                  : hasItems
                  ? 'rgba(16,185,129,0.06)'
                  : 'transparent',
                border: active
                  ? '2px solid #2563eb'
                  : isInRange
                  ? '1px solid #93c5fd'
                  : isToday
                  ? '2px solid #93c5fd'
                  : hasItems
                  ? '1px solid rgba(16,185,129,0.25)'
                  : '1px solid transparent',
                opacity: cell.current ? 1 : 0.3,
                transition: 'all 0.15s ease',
                gap: '3px',
                userSelect: 'none',
                minWidth: 0,
                overflow: 'hidden',
              }}
              title={hasItems ? `${count}${itemLabelSeparator}${count === 1 ? itemLabelSingular : itemLabelPlural}` : ''}
            >
              <span
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: active ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary, #6b7280)',
                }}
              >
                {cell.date.toLocaleDateString('en-US', { weekday: 'short' })}
              </span>
              <span
                style={{
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  color: active ? 'white' : 'var(--text-primary, #111827)',
                }}
              >
                {cell.date.getDate()}
              </span>
              {(showCount || hasItems) && (
                <span
                  style={{
                    fontSize: showCount ? '0.8rem' : '0.68rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: active
                      ? 'rgba(255,255,255,0.95)'
                      : hasItems
                      ? '#2563eb'
                      : 'var(--text-secondary, #6b7280)',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    maxWidth: '100%',
                  }}
                >
                  {showCount
                    ? `(${count})`
                    : `${count}${itemLabelSeparator}${count === 1 ? itemLabelSingular : itemLabelPlural}`}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {compact && (headerAside != null ? headerAside : rangePill)}
      </div>

      {/* Clear Date — only when a day (or range) is picked. Range mode
          also shows the same start/end legend as Sort Papers. */}
      {(hasSelection || range) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 6, padding: '0 4px', fontSize: '0.68rem', color: 'var(--text-secondary)', alignItems: 'center', flexWrap: 'wrap' }}>
          {range && (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ display: 'inline-block', width: 12, height: 10, borderRadius: 2, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }} />
                Has items
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ display: 'inline-block', width: 12, height: 10, borderRadius: 2, background: 'rgba(59,130,246,0.07)', border: '1.5px solid #93c5fd' }} />
                Today
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ display: 'inline-block', width: 12, height: 10, borderRadius: 2, background: 'linear-gradient(135deg,#3b82f6,#2563eb)' }} />
                Start / end
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ display: 'inline-block', width: 12, height: 10, borderRadius: 2, background: 'rgba(59,130,246,0.22)', border: '1px solid #93c5fd' }} />
                In range
              </span>
            </>
          )}
          {hasSelection && (
            <button
              type="button"
              onClick={clearSelection}
              style={{
                marginLeft: 'auto',
                padding: '3px 8px',
                border: 'none',
                borderRadius: 6,
                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.68rem',
                fontWeight: 700,
                boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)',
              }}
              title="Clear date filter"
            >
              <i className="fas fa-times" style={{ marginRight: 4 }}></i>Clear Date
            </button>
          )}
        </div>
      )}
    </div>
  );
}
